import { useRef, useCallback, useEffect } from 'react';
import type { MentionAutocompleteHandle } from '../MentionAutocomplete';
import type { FormattedTextInputHandle } from '../../FormattedTextInput';
import type { MentionFilters, MentionType } from '../MentionTypes';
import type { MinimalDocument } from '../../../types';
import { getFilterKeyForMentionType, normalizeMentionValue } from '../../../utils/mentionUtils';
import { eventBus, EventNames } from '../../../utils/eventBus';
import logger from '../../../utils/simpleLogger';

/**
 * Hook para gestionar menciones y autocompletado
 *
 * Responsabilidades:
 * - Mantener refs de componentes de mención
 * - Insertar y remover menciones del input
 * - Actualizar input con filtros
 * - Manejar click en menciones
 * - Escuchar eventos de edición de mensajes
 *
 * @param params - Parámetros de gestión de menciones
 * @returns Refs y funciones para gestionar menciones
 */

interface UseMentionManagerProps {
  inputMessage: string;
  setInputMessage: (message: string | ((prev: string) => string)) => void;
  contextFilters: MentionFilters;
  setContextFilters: (filters: MentionFilters) => void;
  setSuccessMessage: (message: string | null) => void;
  selectedMinimalDocuments: MinimalDocument[];
  contextSelectedDocs: MinimalDocument[];
  contextSelectedIds: string[];
  deselectContextDocuments: (ids: string[]) => void;
  onDocumentSelect?: (documentIds: string[]) => void;
}

export function useMentionManager({
  inputMessage,
  setInputMessage,
  contextFilters,
  setContextFilters,
  setSuccessMessage,
  selectedMinimalDocuments,
  contextSelectedDocs,
  contextSelectedIds,
  deselectContextDocuments,
  onDocumentSelect,
}: UseMentionManagerProps) {
  // Refs para componentes de mención/input
  const mentionAutocompleteRef = useRef<MentionAutocompleteHandle>(null);
  const formattedInputRef = useRef<FormattedTextInputHandle>(null);

  // Handler para selección de empresa (autocompletado)
  const handleCompanySelect = useCallback(
    (newValue: string) => {
      setInputMessage(newValue);
      // Posicionar el cursor al final después de aceptar la sugerencia
      setTimeout(() => {
        if (formattedInputRef.current) {
          formattedInputRef.current.setCursorToEnd();
        }
      }, 0);
    },
    [setInputMessage]
  );

  // Handler para insertar @ (abrir selector de documentos)
  const handleInsertAt = useCallback(() => {
    logger.info('🔘 [useMentionManager] Botón @ clickeado, abriendo selector de documentos');
    if (mentionAutocompleteRef.current) {
      mentionAutocompleteRef.current.openDocumentSelector();
    } else {
      logger.error('❌ [useMentionManager] mentionAutocompleteRef.current es null');
    }
  }, []);

  // Helper para actualizar el texto del input con las menciones desde filtros
  const updateInputWithFilters = useCallback(
    (filters: {
      company?: string | string[];
      document_type?: string;
      document_period?: string;
    }) => {
      setInputMessage((currentText) => {
        // Crear menciones para cada filtro
        const mentions: string[] = [];

        if (filters.company) {
          const companies = Array.isArray(filters.company) ? filters.company : [filters.company];
          companies.forEach((company) => {
            mentions.push(`@empresa:${company}`);
          });
        }

        if (filters.document_type) {
          mentions.push(`@tipo:${filters.document_type}`);
        }

        if (filters.document_period) {
          mentions.push(`@fecha:${filters.document_period}`);
        }

        // Remover menciones existentes del mismo tipo para evitar duplicados
        let newText = currentText;

        // Remover @empresa: existentes
        if (filters.company !== undefined) {
          newText = newText.replace(/@empresa:[A-Za-zÁÉÍÓÚáéíóúÑñ0-9\s\.\-]+/gi, '').trim();
        }

        // Remover @tipo: existentes
        if (filters.document_type !== undefined) {
          newText = newText.replace(/@tipo:[A-Za-zÁÉÍÓÚáéíóúÑñ0-9\s\.\-]+/gi, '').trim();
        }

        // Remover @fecha: existentes
        if (filters.document_period !== undefined) {
          newText = newText.replace(/@fecha:[A-Za-z0-9]+/gi, '').trim();
        }

        // Si no hay menciones nuevas, retornar el texto limpio
        if (mentions.length === 0) {
          return newText;
        }

        // Agregar nuevas menciones al final
        const finalText = newText + (newText ? ' ' : '') + mentions.join(' ');

        return finalText.trim();
      });

      // Posicionar cursor al final
      setTimeout(() => {
        if (formattedInputRef.current) {
          formattedInputRef.current.setCursorToEnd();
        }
      }, 0);
    },
    [setInputMessage]
  );

  // Manejar click en mención dentro del input
  const handleMentionClick = useCallback(
    (mentionText: string, startIndex: number, endIndex: number) => {
      // Extraer el valor de la mención para eliminarlo de los filtros
      const mentionMatch = mentionText.match(/@(?:(\w+):)?(.+)/);
      let type: string | undefined;
      let value: string | undefined;

      if (mentionMatch) {
        [, type, value] = mentionMatch;
        const mentionType = (type as MentionType) || 'auto';
        const filterKey = getFilterKeyForMentionType(mentionType);

        if (filterKey && value) {
          // Calcular los nuevos filtros (sin el valor eliminado)
          const computeNewFilters = (prev: MentionFilters): MentionFilters => {
            const newFilters = { ...prev };
            const currentValue = newFilters[filterKey];

            if (!currentValue) return newFilters;

            if (Array.isArray(currentValue)) {
              // Es un array, filtrar el valor eliminado
              const filtered = currentValue.filter(
                (v) => normalizeMentionValue(v) !== normalizeMentionValue(value!)
              );
              if (filtered.length === 0) {
                delete newFilters[filterKey];
              } else if (filtered.length === 1) {
                newFilters[filterKey] = filtered[0] as any;
              } else {
                newFilters[filterKey] = filtered as any;
              }
            } else if (normalizeMentionValue(currentValue) === normalizeMentionValue(value!)) {
              // Es un valor único que coincide, eliminarlo
              delete newFilters[filterKey];
            }

            return newFilters;
          };

          // Calcular nuevos filtros y actualizar
          const newFilters = computeNewFilters(contextFilters);
          setContextFilters(newFilters);
        }
      }

      // Remover la mención del texto
      const beforeMention = inputMessage.substring(0, startIndex);
      const afterMention = inputMessage.substring(endIndex);

      // Combinar texto antes y después, preservando un solo espacio
      let newValue = beforeMention.trimEnd() + ' ' + afterMention.trimStart();
      newValue = newValue.replace(/\s+/g, ' ').trim();

      // Si es una mención de documento, deseleccionar el documento del contexto
      if (mentionMatch && type === 'documento' && value) {
        // Eliminar "@" sueltos
        newValue = newValue
          .replace(/\s+@\s+/g, ' ')
          .replace(/^\s*@\s+/, '')
          .replace(/\s+@\s*$/, '')
          .trim();
        newValue = newValue.replace(/^@\s+/, '').replace(/\s+@$/, '').trim();

        // Deseleccionar el documento del contexto
        const documentName = value.trim();
        if (documentName) {
          const normalizeDocName = (name: string) => (name || '').replace(/@/g, '').trim();
          const normalizedDocumentName = normalizeDocName(documentName);

          // Buscar el documento en los documentos seleccionados por nombre
          let docToDeselect = selectedMinimalDocuments.find((doc) => {
            const docName = normalizeDocName(doc.name || doc.original_filename || '');
            return docName === normalizedDocumentName;
          });

          // Intentar coincidencia parcial si no hay exacta
          if (!docToDeselect) {
            docToDeselect = selectedMinimalDocuments.find((doc) => {
              const docName = normalizeDocName(doc.name || doc.original_filename || '');
              return (
                docName.includes(normalizedDocumentName) || normalizedDocumentName.includes(docName)
              );
            });
          }

          // Buscar también en contextSelectedDocs
          if (!docToDeselect) {
            docToDeselect = contextSelectedDocs.find((doc) => {
              const docName = normalizeDocName(doc.name || doc.original_filename || '');
              return (
                docName === normalizedDocumentName ||
                docName.includes(normalizedDocumentName) ||
                normalizedDocumentName.includes(docName)
              );
            });
          }

          if (docToDeselect) {
            logger.info(
              '🗑️ [useMentionManager] Deselecting document from context when chip removed',
              {
                documentId: docToDeselect.id,
                documentName: documentName,
              }
            );
            deselectContextDocuments([docToDeselect.id]);

            // Notificar al componente padre si hay callback
            if (onDocumentSelect && docToDeselect) {
              const remainingDocIds = contextSelectedIds.filter((id) => id !== docToDeselect.id);
              onDocumentSelect(remainingDocIds);
            }
          }
        }
      }

      setInputMessage(newValue);

      // Posicionar cursor en la posición correcta
      setTimeout(() => {
        if (formattedInputRef.current) {
          const cursorPosition = beforeMention.trimEnd().length;
          if (cursorPosition > 0) {
            formattedInputRef.current.setCursorPosition(cursorPosition);
          } else {
            formattedInputRef.current.setCursorToEnd();
          }
        }
      }, 0);
    },
    [
      inputMessage,
      contextFilters,
      setContextFilters,
      selectedMinimalDocuments,
      contextSelectedDocs,
      contextSelectedIds,
      deselectContextDocuments,
      onDocumentSelect,
      setInputMessage,
    ]
  );

  // Escuchar eventos de edición de mensajes
  useEffect(() => {
    const handleMessageEdit = (data: { originalContent: string }) => {
      setInputMessage(data.originalContent);
      setSuccessMessage('Listo para editar. Modifica y reenvía el mensaje.');
      setTimeout(() => setSuccessMessage(null), 3000);
    };

    eventBus.on(EventNames.MESSAGE_EDIT_REQUESTED, handleMessageEdit);

    return () => {
      eventBus.off(EventNames.MESSAGE_EDIT_REQUESTED, handleMessageEdit);
    };
  }, [setInputMessage, setSuccessMessage]);

  return {
    mentionAutocompleteRef,
    formattedInputRef,
    handleCompanySelect,
    handleInsertAt,
    updateInputWithFilters,
    handleMentionClick,
  };
}
